import { BaseHeader } from 'components/header';
import { ReactNode, useState } from 'react';

import { Checkbox, Col, Form, Layout, Row, Skeleton } from 'antd';

import AssessmentClock from 'img/assessment-intro-clock.svg';

import { useMutation } from '@tanstack/react-query';
import { BaseButton } from 'components/button/button-base';
import { Text, Title } from 'components/mvp-typography';
import { ConcussionRXLogo } from 'features/sign-in/v2/concussionRx-logo';
import { AssessmentAPI } from 'lib/api/firebase/apis/api-assessment';
import { PatientAssessmentOverview } from 'lib/documents/patient-assessment/patient-assessment';
import { useGetAssessmentOverview } from 'lib/hooks/tanstack/use-getPatientAssessmentOverview';
import { useGetPendingAssessmentData } from 'lib/hooks/tanstack/use-getPendingAssessmentData';
import { oneThirdCol, twoThirdCol } from 'lib/utils/antd';
import { getPatientLinkUrl } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { isMobile } from 'react-device-detect';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { PatientRoutes } from 'routes';
import './assessment-intro.scss';
import { Step } from './assessment-step';

const TEXT_CONFIG: {
  default: Record<
    string,
    (
      assessmentType: PatientAssessmentOverview['nextAssessmentType']
    ) => ReactNode
  >;
} = {
  default: {
    heroTitle: assessmentType =>
      assessmentType === 'post-injury'
        ? `Concussion isn't just a "yes" or "no" diagnosis.`
        : 'Understanding your brain function matters, even before an injury',
    heroDescription: assessmentType =>
      assessmentType === 'post-injury' ? (
        <Text>
          No two concussions are the same. ConcussionRx precisely defines and
          breaks down your concussion by its &quot;complexity subtype&quot;,
          offering your healthcare provider a concussion assessment prescription
          for you, and specific symptom highlights for comparison over time to
          make sure you’re achieving your full recovery. <br />
          <br />
          For the most accurate results, it’s best to set aside 1 hour to
          complete these steps all in one go. If you have to take a break, don’t
          worry,{' '}
          <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            you’ll have 72 hours
          </span>{' '}
          to complete everything.
        </Text>
      ) : (
        <Text>
          No two brains are the same. ConcussionRx establishes your individual
          performance profile across critical systems including neurocognitive,
          oculomotor, vestibular, and emotional function. These results serve as
          your personal benchmark so that, if an injury ever occurs, your
          clinical team can compare post-injury scores to your Baseline to guide
          Return to Learn, Return to Sport, and overall recovery goals.
          <br />
          <br />
          For the most accurate results, please set aside about 45 minutes to
          complete the test in one sitting - most complete it within 30 minutes,
          but it’s important you have enough time to finish the test if needed.{' '}
          <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            You’ll have 72 hours
          </span>{' '}
          to complete everything.
        </Text>
      ),
    checkboxLabel: () =>
      `I acknowledge the 72 hour time limit to complete these steps. Otherwise, I'll have to restart the assessment to maintain medical accuracy.`,
    buttonText: () => `I'm ready!`
  }
  // Add more assessment types here
  // short: { ... }
  // full: { ... }
};

const stepsData = [
  {
    duration: '10-15 mins',
    title: 'Answer a series of questionnaires',
    description: 'To understand your functioning across multiple areas',
    index: 1,
    endNode: false
  },
  {
    duration: '20 mins',
    title: 'Complete a neurocognitive screening tool',
    description: 'To evaluate your cognitive performance',
    index: 2,
    endNode: true
  }
];

function useStartAssessmentMutation() {
  return useMutation({
    mutationFn: AssessmentAPI.start
  });
}

export function AssessmentIntro() {
  const [checked, setChecked] = useState(false);
  const history = useHistory();
  const { patientId } = useParams<{ patientId: string }>();

  const assessmentOverview = useGetAssessmentOverview(patientId);
  const pendingAssessmentData = useGetPendingAssessmentData(patientId);

  const startAssessmentMut = useStartAssessmentMutation();

  const continueUrl = getPatientLinkUrl(
    PatientRoutes.AssessmentControlFlow,
    patientId
  );

  // eslint-disable-next-line
  async function formSubmit(_e: Event) {
    await startAssessmentMut.mutateAsync({
      patientId: patientId
    });
    history.push(continueUrl);
  }

  const patientIsDoingAnAssessment =
    pendingAssessmentData.data?.status === true &&
    pendingAssessmentData.data.overview.status === 'started';
  if (
    startAssessmentMut.isLoading ||
    assessmentOverview.isLoading ||
    pendingAssessmentData.isLoading
  )
    return <Skeleton active />;

  /**
   * Redirect the user away from this page if they have already started an assessment
   */
  if (patientIsDoingAnAssessment) {
    return <Redirect to={continueUrl} />;
  }

  if (assessmentOverview.error || !assessmentOverview.data) {
    throw new Error(
      `Failed to fetch assessment data for Test-taker (id=${patientId})`
    );
  }

  /**
   * Show an error message that assessments cannot be completed on mobile devices
   */
  if (isMobile) {
    return <CrxMobileDeviceAssessmentBlocked />;
  }

  // return <QuestionnaireInjury />
  return (
    <Layout className="assessment-container">
      <BaseHeader />

      {/*hero container*/}
      <div className="assessment-container__hero">
        <div className="hero__container">
          <div className="hero__title crx-typography h1">
            {TEXT_CONFIG.default.heroTitle(
              assessmentOverview.data.nextAssessmentType
            )}
            <br></br>
          </div>
          <div className="hero__description crx-typography body-normal">
            <br></br>
            <br></br>
            {TEXT_CONFIG.default.heroDescription(
              assessmentOverview.data.nextAssessmentType
            )}
          </div>
        </div>
      </div>

      {/*steps*/}
      <div className="assessment-steps-container">
        <Row gutter={[16, 16]}>
          <Col {...twoThirdCol} style={{ height: '100%' }}>
            {stepsData.map((data, i) => (
              <Step
                key={i}
                duration={data.duration}
                title={data.title}
                description={data.description}
                index={data.index}
                endNode={data.endNode}
              />
            ))}
          </Col>
          <Col {...oneThirdCol}>
            <img width={340} src={AssessmentClock} />
          </Col>
        </Row>
      </div>

      {/*checkbox for time limit*/}
      <Form
        name="acknowledgement"
        onFinish={e => {
          formSubmit(e);
        }}
      >
        <div>
          <Row className="checkbox-container" style={{ marginBottom: '16px' }}>
            {
              /**
               * 2025-12-16
               * Change requested: Only show the consent checkbox for non-baseline tests
               */
              assessmentOverview.data.nextAssessmentType !== 'baseline' && (
                <Form.Item
                  name="72-hr-acknowledgement"
                  valuePropName="checked"
                  required={true}
                  rules={[
                    {
                      validator() {
                        // _, value
                        // console.log("value: ", value, _, checked);``
                        if (checked === true) return Promise.resolve();
                        return Promise.reject(
                          'Please acknowledge the 72 hour agreement'
                        );
                      }
                    }
                  ]}
                >
                  <Checkbox
                    checked={checked}
                    onChange={e => {
                      setChecked(e.target.checked);
                    }}
                  />
                  <span className="checkbox-container__label crx-typography label-small">
                    {TEXT_CONFIG.default.checkboxLabel(
                      assessmentOverview.data.nextAssessmentType
                    )}
                  </span>
                </Form.Item>
              )
            }
          </Row>
          <Row style={{ marginLeft: '110px' }}>
            <Form.Item>
              <BaseButton
                className="crx-typography button-big"
                htmlType="submit"
              >
                {TEXT_CONFIG.default.buttonText(
                  assessmentOverview.data.nextAssessmentType
                )}
              </BaseButton>
            </Form.Item>
          </Row>
        </div>
      </Form>
    </Layout>
  );
}

/**
 * Show an error message that assessments cannot be completed on mobile devices
 */
export function CrxMobileDeviceAssessmentBlocked() {
  return (
    <Layout className="assessment-container">
      <ConcussionRXLogo />
      <Title className="hero_title crx-typoghraphy h1">
        Please use a wide-screen device
      </Title>
      <Text>
        ConcussionRx assessments are designed to be done on wide-screen devices
        like a desktop computer.
      </Text>
      <Text>
        Please login on a device with a larger screen size to start or complete
        your assessment.
      </Text>
    </Layout>
  );
}
